import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Check, ArrowRight, Hammer, Lightbulb, Wrench, Zap, LampCeiling, SunDim, SlidersHorizontal, Sparkles } from "lucide-react";
import { SERVICES, EXTRA_SERVICES, LIGHTING } from "../data/content";
import { Reveal } from "../components/Reveal";
import { Footer } from "../components/Footer";
import { Seo } from "../components/Seo";

const EXTRA_ICONS = { demontavimas: Hammer, sviestuvai: Lightbulb, "kiti-darbai": Wrench };
const LIGHTING_ICONS = {
  "led-linijos": Zap,
  taskiniai: Lightbulb,
  konturinis: SunDim,
  sietynai: LampCeiling,
  profiiliai: Sparkles,
  valdymas: SlidersHorizontal,
};

export default function Services() {
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const t = setTimeout(() => {
        document.getElementById(location.hash.slice(1))?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 300);
      return () => clearTimeout(t);
    }
  }, [location]);

  return (
    <main data-testid="services-page">
      <Seo
        title="Matinės, blizgios, LED ir medžiaginės lubos — ART Ceiling"
        description="Visos lubų sistemos ir apšvietimo sprendimai: matinės, blizgios, LED linijos, medžiaginės lubos, lubų demontavimas, šviestuvų montavimas. Dirbame visoje Lietuvoje."
      />
      <section className="grain relative overflow-hidden bg-navy-950 pb-20 pt-32 lg:pb-28 lg:pt-44">
        <div className="pointer-events-none absolute -right-40 -top-20 h-96 w-96 rounded-full bg-sky-500/10 blur-3xl" />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-12">
          <Reveal>
            <p className="text-xs font-bold uppercase tracking-[0.3em] text-sky-300">Paslaugos</p>
            <h1 className="mt-4 max-w-2xl font-heading text-4xl font-extrabold tracking-tight text-white sm:text-5xl">
              Keturios sistemos. Vienas standartas — tobulumas.
            </h1>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-slate-400 sm:text-lg">
              Nuo klasikinių matinių iki architektūrinių LED linijų — kiekviena sistema montuojama per vieną dieną su 10 metų garantija.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-white py-16 sm:py-24">
        <div className="mx-auto max-w-7xl space-y-20 px-4 sm:px-6 lg:space-y-28 lg:px-12">
          {SERVICES.map((s, i) => (
            <Reveal key={s.id}>
              <div
                id={s.id}
                data-testid={`service-section-${s.id}`}
                className={`grid grid-cols-1 items-center gap-10 lg:grid-cols-2 lg:gap-16 ${
                  i % 2 === 1 ? "lg:[&>*:first-child]:order-2" : ""
                }`}
              >
                <div className="group relative">
                  <div className="overflow-hidden rounded-[1.75rem] border border-slate-200 shadow-[0_30px_60px_-30px_rgba(15,23,42,0.3)]">
                    <img
                      src={s.image_url}
                      alt={s.title}
                      loading="lazy"
                      className="aspect-[4/3] w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                    />
                  </div>
                </div>
                <div>
                  <span className="font-heading text-6xl font-extrabold text-navy-900/8 sm:text-7xl">
                    0{i + 1}
                  </span>
                  <h2 className="mt-2 font-heading text-2xl font-bold tracking-tight text-navy-900 sm:text-3xl">
                    {s.title}
                  </h2>
                  <p className="mt-2 text-base font-medium text-sky-600">{s.subtitle}</p>
                  <p className="mt-5 text-base leading-relaxed text-slate-600">{s.description}</p>
                  <ul className="mt-7 grid grid-cols-1 gap-3 sm:grid-cols-2">
                    {s.features.map((f) => (
                      <li key={f} className="flex items-start gap-2.5 text-sm text-slate-700">
                        <Check className="mt-0.5 h-4 w-4 shrink-0 text-sky-600" />
                        {f}
                      </li>
                    ))}
                  </ul>
                  <Link
                    to="/#matavimas"
                    data-testid={`service-cta-${s.id}`}
                    className="group mt-9 inline-flex items-center gap-2 rounded-full bg-navy-900 px-6 py-3.5 text-sm font-semibold text-white transition-all duration-300 hover:bg-navy-800"
                  >
                    Užsisakyti nemokamą matavimą
                    <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                  </Link>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* LIGHTING SOLUTIONS */}
      <section className="grain relative overflow-hidden bg-navy-950 py-16 sm:py-24 lg:py-28" data-testid="lighting-section">
        <div className="pointer-events-none absolute -right-40 top-0 h-96 w-96 rounded-full bg-sky-500/10 blur-3xl" />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-12">
          <Reveal>
            <p className="text-xs font-bold uppercase tracking-[0.3em] text-sky-300">Apšvietimas</p>
            <h2 className="mt-4 max-w-2xl font-heading text-2xl font-bold tracking-tight text-white sm:text-3xl lg:text-4xl">
              Visi apšvietimo sprendimai
            </h2>
            <p className="mt-5 max-w-xl text-base leading-relaxed text-slate-400">
              Nuo architektūrinių LED linijų iki išmanaus valdymo — apšvietimą projektuojame ir montuojame kartu su lubomis.
            </p>
          </Reveal>

          <div className="mt-12 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {LIGHTING.map((l, i) => {
              const Icon = LIGHTING_ICONS[l.id];
              return (
                <Reveal key={l.id} delay={i * 0.07}>
                  <div
                    data-testid={`lighting-card-${l.id}`}
                    className="group h-full rounded-2xl border border-white/10 bg-white/5 p-7 backdrop-blur-sm transition-all duration-500 hover:-translate-y-1 hover:border-white/25 hover:bg-white/10"
                  >
                    <Icon className="h-7 w-7 text-sky-300 transition-transform duration-500 group-hover:scale-110" />
                    <h3 className="mt-5 font-heading text-lg font-semibold text-white">{l.title}</h3>
                    <p className="mt-2.5 text-sm leading-relaxed text-slate-400">{l.description}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>

          <Reveal delay={0.2}>
            <Link
              to="/#matavimas"
              data-testid="lighting-cta"
              className="group mt-10 inline-flex items-center gap-2 rounded-full bg-sky-400 px-7 py-3.5 text-sm font-bold text-navy-950 transition-all duration-300 hover:bg-sky-300"
            >
              Pasitarti dėl apšvietimo
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* EXTRA SERVICES */}
      <section className="bg-slate-50 py-16 sm:py-24" data-testid="extra-services-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-12">
          <Reveal>
            <p className="text-xs font-bold uppercase tracking-[0.3em] text-sky-600">Papildomos paslaugos</p>
            <h2 className="mt-4 max-w-xl font-heading text-2xl font-bold tracking-tight text-navy-900 sm:text-3xl">
              Viskas, ko reikia jūsų luboms — vienoje vietoje
            </h2>
          </Reveal>

          <div className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-3">
            {EXTRA_SERVICES.map((s, i) => {
              const Icon = EXTRA_ICONS[s.id];
              return (
                <Reveal key={s.id} delay={i * 0.1}>
                  <div
                    data-testid={`extra-service-card-${s.id}`}
                    className="group flex h-full flex-col rounded-2xl border border-slate-200 bg-white p-8 transition-all duration-500 hover:-translate-y-1 hover:border-navy-900/20 hover:shadow-[0_24px_50px_-20px_rgba(15,23,42,0.15)]"
                  >
                    <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-navy-950 transition-transform duration-500 group-hover:scale-105">
                      <Icon className="h-6 w-6 text-sky-300" />
                    </div>
                    <h3 className="mt-6 font-heading text-lg font-semibold text-navy-900">{s.title}</h3>
                    <p className="mt-3 flex-1 text-sm leading-relaxed text-slate-600">{s.description}</p>
                    <Link
                      to="/#matavimas"
                      data-testid={`extra-service-cta-${s.id}`}
                      className="mt-6 inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-sky-600 transition-colors hover:text-navy-900"
                    >
                      Užsakyti
                      <ArrowRight className="h-3.5 w-3.5" />
                    </Link>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}
